import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser, getToken, roleDisplayLabel } from "@/lib/auth";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Trash2, MessagesSquare, ArrowUp } from "lucide-react";

/**
 * Team Chat — single shared room.
 * • Native WebSocket for live push (server-side visibility gate is authoritative).
 * • REST fallback + initial history load, scroll-up loads older 100 at a time.
 * • Admin/SuperAdmin can moderate (soft-delete).
 * • Money-blindness rule is untouched — no money fields are read or displayed here.
 */

const roleColour = (role) => ({
  Admin: "bg-neutral-900 text-white",
  SuperAdmin: "bg-black text-white",
  Supervisor: "bg-blue-100 text-blue-800",
  Promoter: "bg-red-100 text-red-800",
  IT: "bg-purple-100 text-purple-800",
  "Project Lead": "bg-indigo-100 text-indigo-800",
  "Coca-Cola": "bg-green-100 text-green-800",
  Client: "bg-green-100 text-green-800",
}[role] || "bg-gray-100 text-gray-800");

const displayRole = (r) => (r === "Client" ? "Coca-Cola" : r);

const fmtIST = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit",
    day: "2-digit", month: "short", hour12: true,
  });
};

export default function TeamChat() {
  const u = getUser();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const [wsAlive, setWsAlive] = useState(false);
  const scrollerRef = useRef(null);
  const wsRef = useRef(null);
  const messagesRef = useRef([]);   // mirror of state for use inside stable interval callback
  const canModerate = u?.role === "Admin" || u?.role === "SuperAdmin";

  // Keep a synchronous ref of the current message set so the 3s poller can dedupe cheaply.
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const appendUnique = (incoming) => {
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      if (fresh.length === 0) return prev;
      const next = [...prev, ...fresh].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      return next;
    });
    setTimeout(() => scrollToBottom(), 30);
  };

  // ---------- Initial history load (waits for token to exist) ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Small delay so localStorage token is definitely attached to axios via the interceptor
      // (prevents the "No messages yet" flash on the very first render right after login).
      if (!getToken()) return;
      try {
        const { data } = await api.get("/chat/messages", { params: { limit: 100 } });
        if (!cancelled) {
          setMessages(data || []);
          setNoMore((data || []).length < 100);
          setTimeout(() => scrollToBottom(), 40);
        }
      } catch { toast.error("Failed to load chat history"); }
      try { await api.post("/chat/mark-read"); } catch { /* ignore */ }
      window.dispatchEvent(new Event("focus"));
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------- Live push #1 — WebSocket (best-effort, may not survive every ingress) ----------
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = base.replace(/^http/, "ws");
    const wsUrl = `${wsBase}/api/chat/ws?token=${encodeURIComponent(token)}`;
    let ws;
    let stopped = false;
    let retry = 0;
    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { retry = 0; setWsAlive(true); };
        ws.onmessage = (evt) => {
          let payload;
          try { payload = JSON.parse(evt.data); } catch { return; }
          if (payload.type === "message") {
            appendUnique([payload.data]);
            api.post("/chat/mark-read").catch(() => {});
          } else if (payload.type === "delete") {
            setMessages((prev) => prev.map((m) => m.id === payload.id ? { ...m, is_deleted: true } : m));
          }
        };
        ws.onclose = () => {
          setWsAlive(false);
          if (stopped) return;
          retry += 1;
          const delay = Math.min(15000, 500 * Math.pow(2, retry));
          setTimeout(connect, delay);
        };
        ws.onerror = () => { /* onclose will handle retry */ };
      } catch { /* let onclose retry */ }
    };
    connect();
    return () => { stopped = true; try { ws && ws.close(); } catch { /* nothing */ } };
  }, []);

  // ---------- Live push #2 — short-interval poll (guaranteed delivery within ~3s) ----------
  // This is the authoritative delivery channel — WS above is an optimisation. Even when the
  // WebSocket is blocked by an ingress, this poll ensures live messages land in every open
  // permitted viewer's window within a couple seconds. The server enforces the visibility
  // gate on every response, so a blocked viewer will never see the other side's messages here.
  useEffect(() => {
    if (!getToken()) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;   // pause when tab hidden — resumes on visibilitychange
      try {
        // We ask for the 50 most recent including deleted, so an already-open session
        // sees moderation deletes surface within ~3 s even if the WebSocket is silent.
        const { data } = await api.get("/chat/messages", { params: { limit: 50, include_deleted: true } });
        if (!Array.isArray(data) || data.length === 0) return;
        const deletedIds = data.filter(m => m.is_deleted).map(m => m.id);
        if (deletedIds.length) {
          setMessages((prev) => prev.map(m => deletedIds.includes(m.id) ? { ...m, is_deleted: true } : m));
        }
        appendUnique(data.filter(m => !m.is_deleted));
        api.post("/chat/mark-read").catch(() => {});
      } catch { /* transient — try again next tick */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stopped = true; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { text: t });
      appendUnique([data]);
      setText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const loadOlder = async () => {
    if (loadingMore || noMore || messages.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = messages[0];
      const el = scrollerRef.current;
      const prevScrollHeight = el?.scrollHeight || 0;
      const { data } = await api.get("/chat/messages", { params: { before: oldest.id, limit: 100 } });
      if ((data || []).length === 0) { setNoMore(true); return; }
      setMessages((prev) => [...data, ...prev]);
      // Preserve scroll position so the user doesn't get jumped around.
      setTimeout(() => {
        if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
      }, 20);
    } catch { toast.error("Failed to load older messages"); }
    finally { setLoadingMore(false); }
  };

  const remove = async (id) => {
    if (!canModerate) return;
    if (!window.confirm("Delete this message? This is logged in the Audit trail.")) return;
    try {
      await api.delete(`/chat/messages/${id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const visible = useMemo(() => messages.filter((m) => !m.is_deleted), [messages]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Team Chat" subtitle="One shared room · IST" />
      <div className="flex flex-1">
        <SideNav />
        <main className="flex-1 max-w-4xl mx-auto w-full p-4 md:p-6 flex flex-col min-h-0" data-testid="team-chat-page">

          <Card className="flex-1 flex flex-col overflow-hidden rounded-2xl border-gray-200 min-h-[70vh]">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-white">
              <MessagesSquare className="h-4 w-4 text-brand-red" />
              <div className="font-display font-bold text-sm">Team Chat</div>
              <div className="text-[11px] text-gray-500">
                Promoter and Coca-Cola messages are private to their own audience.
              </div>
            </div>

            <div
              ref={scrollerRef}
              onScroll={(e) => { if (e.target.scrollTop < 40) loadOlder(); }}
              className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-gray-50"
              data-testid="chat-scroller"
            >
              {!noMore && messages.length > 0 && (
                <div className="text-center">
                  <button onClick={loadOlder} disabled={loadingMore} data-testid="load-older-btn"
                    className="text-xs text-gray-500 hover:text-brand-red inline-flex items-center gap-1">
                    <ArrowUp className="h-3 w-3" /> {loadingMore ? "Loading…" : "Load older messages"}
                  </button>
                </div>
              )}
              {visible.length === 0 && (
                <div className="text-center text-gray-400 text-sm py-16" data-testid="chat-empty">
                  No messages yet. Say hi!
                </div>
              )}
              {visible.map((m) => {
                const mine = m.sender_id === u?.id;
                const dr = displayRole(m.sender_role);
                return (
                  <div key={m.id} data-testid={`chat-msg-${m.id}`}
                    className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm ${
                      mine ? "bg-brand-red text-white" : "bg-white border border-gray-200"
                    }`}>
                      <div className={`flex items-center gap-2 mb-0.5 text-[11px] ${mine ? "text-white/80" : "text-gray-500"}`}>
                        <span className="font-semibold">{m.sender_name}</span>
                        <Badge className={`${roleColour(dr)} px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider`}>
                          {roleDisplayLabel(m.sender_role)}
                        </Badge>
                        <span>· {fmtIST(m.created_at)}</span>
                        {canModerate && (
                          <button data-testid={`chat-delete-${m.id}`} onClick={() => remove(m.id)}
                            className={`ml-1 hover:opacity-100 opacity-60 ${mine ? "text-white/80" : "text-gray-400 hover:text-brand-red"}`}
                            aria-label="Delete message">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-sm whitespace-pre-wrap break-words" data-testid={`chat-text-${m.id}`}>{m.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-gray-100 p-3 bg-white flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                placeholder="Write a message… (Enter to send · Shift+Enter for new line)"
                className="flex-1 resize-none border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30"
              />
              <Button
                data-testid="chat-send-btn"
                onClick={send} disabled={!text.trim() || sending}
                className="bg-brand-red hover:bg-[#B31721] h-11 px-4">
                <Send className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Send</span>
              </Button>
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
